import { jsx as _jsx } from "react/jsx-runtime";
import { Carousel, CarouselVariant, ProductTileMini, ProductTileMiniVariant, StickyBar, StickyBarVariant, useScrollPosition, useDebounce, } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { ProductImageSizesPortraitEnum, } from '@as-react/common-types';
import { determineSelectedSku } from '@as-react/common-utils';
import { Box } from '@yonderland/andes-react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NAVIGATION_HORIZONTAL, NAVIGATION_HORIZONTAL_COLLAPSE, NAVIGATION_HEIGHT_MOBILE, } from '../../../constants/DOMElementsConstants';
import { DELAY } from '../../../constants/GeneralConstants';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { SLIDE_SPACING } from '../../../constants/SliderConstants';
import { getPortraitImageSizeUrlByProductCode } from '../../../util/ImageUtil';
import { isProductOutOfStock, isProductWithColorOutOfStock } from '../../../util/ProductUtil';
export const MAIN_COMPARE_CAROUSEL_ID = 'compare-carousel';
const CAROUSEL_EL_SELECTOR = '.as-m-carousel--product-tile-normal';
const TOUCH_END_LENGTH = 50;
export const CompareStickyBarMobile = ({ comparisonProducts, onCloseClick, addToBasket, setSwiperInstance, }) => {
    const { t } = useTranslation([I18N_NAMESPACES.product]);
    const { productImageConfig } = useAemConfig();
    const [hidden, setHidden] = useState(true);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [navigationEl, setNavigationEl] = useState(null);
    const [carouselElement, setCarouselElement] = useState(null);
    const [compareCarouselSwiper, setCompareCarouselSwiper] = useState(null);
    const [productTileVariant, setProductTileVariant] = useState(ProductTileMiniVariant.NORMAL);
    const stickyRef = useRef(null);
    const [touchStartY, setTouchStartY] = useState(null);
    const [stickyBarHeight, setStickyBarHeight] = useState(null);
    const [isTouchInProgress, setIsTouchInProgress] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const debouncedSetTileVariant = useDebounce(setProductTileVariant, DELAY.DELAY_300);
    const productTileMiniTranslations = {
        outOfStock: t(`${I18N_NAMESPACES.product}:product.temporarilyOutOfStock`),
        otherColorAvailable: t(`${I18N_NAMESPACES.product}:wishlist.outOfStock.other.color.available`),
    };
    const toggleStickyBar = () => {
        const nextIsCollapsedState = !isCollapsed;
        setIsCollapsed(nextIsCollapsedState);
        nextIsCollapsedState
            ? debouncedSetTileVariant(ProductTileMiniVariant.COMPACT)
            : setProductTileVariant(ProductTileMiniVariant.NORMAL);
    };
    const setSwiper = (swiper) => {
        setCompareCarouselSwiper(swiper);
        setSwiperInstance(swiper);
    };
    const compareCarouselConfig = {
        variant: CarouselVariant.PRODUCT_TILE_NORMAL,
        showPagination: false,
        configurationOverrides: {
            preventClicks: true,
            slidesPerView: 'auto',
            spaceBetween: SLIDE_SPACING.MOBILE,
            watchOverflow: true,
            getSwiper: setSwiper,
        },
    };
    const compareSlide = useCallback((product) => {
        const activeColour = product.colours.find((colour) => colour.colourId === product.colourId);
        const activeSku = determineSelectedSku(activeColour);
        const activeSize = activeColour.sizes.find(size => size.sku === activeSku);
        const productOutOfStock = isProductOutOfStock(product);
        const colorOutOfStock = isProductWithColorOutOfStock(product);
        const getImage = () => getPortraitImageSizeUrlByProductCode(product.productCode, activeColour.image, productImageConfig.cdnDomains, ProductImageSizesPortraitEnum.basket);
        const onAddToBasketClick = () => addToBasket({
            product,
            colorId: activeColour.colourId,
            sku: activeSku,
        });
        const onCloseButtonClick = () => onCloseClick(product.productCode, activeColour.colourId);
        return (_jsx("div", { children: _jsx(ProductTileMini, { variant: productTileVariant, imageAlt: product.title, imageSrc: getImage(), outOfStock: productOutOfStock, colorOutOfStock: productOutOfStock ? false : colorOutOfStock, onAddToBasketClick: (activeSize === null || activeSize === void 0 ? void 0 : activeSize.storeSourceExcluded) ? null : onAddToBasketClick, onCloseButtonClick: onCloseButtonClick, translations: productTileMiniTranslations }) }, `${product.productCode}${product.colourId}`));
    }, [comparisonProducts, addToBasket, productTileVariant]);
    const onTouchStart = (e) => {
        const touch = e.touches[0];
        setTouchStartY(touch.clientY);
        setStickyBarHeight(stickyRef.current.offsetHeight);
        setIsTouchInProgress(true);
    };
    const onTouchMove = (e) => {
        if (isTouchInProgress) {
            const touch = e.touches[0];
            const touchLength = touchStartY - touch.clientY;
            if ((isCollapsed && touchLength < 0) || (!isCollapsed && touchLength > 0)) {
                const height = stickyBarHeight + touch.clientY - touchStartY;
                stickyRef.current.style.height = `${height}px`;
                stickyRef.current.style.maxHeight = `${height}px`;
                stickyRef.current.style.transitionProperty = 'none';
                if (Math.abs(touchLength) > TOUCH_END_LENGTH) {
                    isCollapsed
                        ? setProductTileVariant(ProductTileMiniVariant.NORMAL)
                        : debouncedSetTileVariant(ProductTileMiniVariant.COMPACT);
                    setIsCollapsed(!isCollapsed);
                    setIsTouchInProgress(false);
                    setTouchStartY(null);
                    stickyRef.current.style.height = '';
                    stickyRef.current.style.maxHeight = '';
                    stickyRef.current.style.transitionProperty = '';
                }
            }
        }
    };
    const onTouchEnd = () => {
        stickyRef.current.style.height = '';
        stickyRef.current.style.maxHeight = '';
        stickyRef.current.style.transitionProperty = '';
        setStickyBarHeight(null);
        setIsTouchInProgress(false);
    };
    useEffect(() => {
        if (compareCarouselSwiper !== null) {
            compareCarouselSwiper.update();
        }
    }, [comparisonProducts, compareCarouselSwiper]);
    useEffect(() => {
        const navigation = document.querySelector(NAVIGATION_HORIZONTAL);
        const anchorEl = document.querySelector(CAROUSEL_EL_SELECTOR);
        setNavigationEl(navigation);
        setCarouselElement(anchorEl);
        // This needed because the navigation menu gets hidden after the page loads with a delay
        setTimeout(() => setShouldRender(true), DELAY.DELAY_100);
        return () => setSwiperInstance(null);
    }, []);
    useScrollPosition(({ currPos }) => {
        var _a;
        if (carouselElement) {
            const positionTop = Math.abs(currPos.y);
            const tmpIsNavDisplayed = !!navigationEl && !navigationEl.classList.contains(NAVIGATION_HORIZONTAL_COLLAPSE);
            const $mainCarousel = document.getElementById(MAIN_COMPARE_CAROUSEL_ID);
            const whenToSticky = $mainCarousel.offsetHeight - ((_a = stickyRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) + $mainCarousel.offsetTop;
            setHidden((tmpIsNavDisplayed ? positionTop + NAVIGATION_HEIGHT_MOBILE : positionTop) < whenToSticky);
        }
    }, [navigationEl, carouselElement], null, false, 0);
    if (!shouldRender || !navigationEl) {
        return null;
    }
    return createPortal(_jsx(StickyBar, { innerRef: stickyRef, onSwipe: {
            onTouchStart,
            onTouchMove,
            onTouchEnd,
        }, isHidden: hidden, variant: StickyBarVariant.COMPARE_MOBILE, collapsed: isCollapsed, onToggleClick: toggleStickyBar, children: _jsx(Box, { paddingBlockStart: "3", paddingBlockEnd: productTileVariant === ProductTileMiniVariant.COMPACT ? '0' : '1', children: _jsx(Carousel, Object.assign({}, compareCarouselConfig, { children: comparisonProducts.map(compareSlide) })) }) }), navigationEl);
};
